import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faSync,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faInfoCircle,
  faSearch,
  faEye,
  faFileAlt,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import JSONViewer from "../tasks/JSONViewer";

const DatasetMonitoring = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [durinStatus, setDurinStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveContent, setArchiveContent] = useState(null);
  const [viewingArchive, setViewingArchive] = useState(false);
  const [limit, setLimit] = useState(10);
  const [jsonUrl, setJsonUrl] = useState(null);

  useEffect(() => {
    checkDurinDataset();
  }, []);

  const checkDurinDataset = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get dataset stats
      const statsResponse = await fetch(
        "https://docminer-ts-ms-gwbzd6h2gddscrgw.francecentral-01.azurewebsites.net/api/v1/dataset/stats"
      );
      const statsData = await statsResponse.json();

      if (statsData.configured) {
        setDurinStatus({
          exists: statsData.containerExists,
          isEmpty: statsData.totalArchives === 0,
          totalArchives: statsData.totalArchives || 0,
          lastChecked: new Date().toISOString(),
          lastArchived: statsData.lastArchived,
          status: statsData.totalArchives > 0 ? "healthy" : "empty",
        });

        // Get the archive list if container exists
        if (statsData.containerExists) {
          await fetchArchiveList();
        }
      } else {
        setDurinStatus({
          exists: false,
          isEmpty: true,
          lastChecked: new Date().toISOString(),
          status: "not_configured",
        });
      }
    } catch (err) {
      console.error("Durin dataset check error:", err);
      setError("Failed to check durin-ts-ds dataset");
    } finally {
      setLoading(false);
    }
  };

  const fetchArchiveList = async () => {
    try {
      const response = await fetch(
        `https://docminer-ts-ms-gwbzd6h2gddscrgw.francecentral-01.azurewebsites.net/api/v1/dataset/archives?limit=${limit}`
      );
      const data = await response.json();

      if (data.configured && data.archives) {
        setArchives(
          data.archives.map((archive) => ({
            ...archive,
            // Convert string date to Date object if it's not already
            timestamp:
              typeof archive.timestamp === "string"
                ? new Date(archive.timestamp)
                : archive.timestamp,
          }))
        );
      } else {
        setArchives([]);
      }
    } catch (err) {
      console.error("Failed to fetch archive list:", err);
      setError("Failed to fetch archive list");
    }
  };

  const handleViewArchive = async (archive) => {
    try {
      setViewingArchive(true);
      setSelectedArchive(archive);

      // Create a URL for the JSONViewer component
      const archiveUrl = `https://docminer-ts-ms-gwbzd6h2gddscrgw.francecentral-01.azurewebsites.net/api/v1/dataset/archives/${archive.name}`;
      setJsonUrl(archiveUrl);
    } catch (err) {
      console.error("Failed to prepare archive for viewing:", err);
      setError("Failed to prepare archive for viewing");
    } finally {
      setViewingArchive(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await checkDurinDataset();
    setRefreshing(false);
  };

  const handleCloseViewer = () => {
    setSelectedArchive(null);
    setJsonUrl(null);
  };

  const handleChangeLimit = (newLimit) => {
    setLimit(newLimit);
    fetchArchiveList();
  };

  const getStatusDisplay = () => {
    if (!durinStatus) return null;

    if (!durinStatus.exists) {
      return {
        icon: faExclamationTriangle,
        color: "has-text-warning",
        text: "Dataset container does not exist",
        bgColor: "has-background-warning-light",
      };
    }

    if (durinStatus.isEmpty) {
      return {
        icon: faInfoCircle,
        color: "has-text-info",
        text: "Dataset is empty",
        bgColor: "has-background-info-light",
      };
    }

    if (durinStatus.status === "healthy") {
      return {
        icon: faCheckCircle,
        color: "has-text-success",
        text: "Dataset is healthy",
        bgColor: "has-background-success-light",
      };
    }

    if (durinStatus.status === "not_configured") {
      return {
        icon: faExclamationTriangle,
        color: "has-text-danger",
        text: "Azure Storage not configured",
        bgColor: "has-background-danger-light",
      };
    }

    return {
      icon: faExclamationTriangle,
      color: "has-text-warning",
      text: "Status unknown",
      bgColor: "has-background-warning-light",
    };
  };

  if (loading) {
    return (
      <div className="container p-6">
        <div className="has-text-centered">
          <FontAwesomeIcon
            icon={faSync}
            spin
            size="2x"
            className="has-text-primary"
          />
          <p className="mt-4">Checking durin-ts-ds dataset...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container p-6">
        <div className="notification is-danger">
          <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
          {error}
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();

  return (
    <div className="container p-6">
      {/* Header */}
      <div className="level mb-6">
        <div className="level-left">
          <div>
            <h1 className="title is-2">
              <FontAwesomeIcon icon={faDatabase} className="mr-3" />
              Dataset Monitoring
            </h1>
            <p className="subtitle">Check durin-ts-ds dataset status</p>
          </div>
        </div>
        <div className="level-right">
          <button
            className={`button is-primary ${refreshing ? "is-loading" : ""}`}
            onClick={handleRefresh}
          >
            <FontAwesomeIcon icon={faSync} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Dataset Status Card */}
      <div className="card neumorphic mb-6">
        <div className="card-header">
          <p className="card-header-title">
            <FontAwesomeIcon icon={faDatabase} className="mr-2" />
            durin-ts-ds Dataset
          </p>
        </div>
        <div className="card-content">
          <div className={`notification ${statusDisplay?.bgColor}`}>
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <span className={`icon is-medium ${statusDisplay?.color}`}>
                    <FontAwesomeIcon icon={statusDisplay?.icon} size="lg" />
                  </span>
                  <div className="ml-3">
                    <p className="title is-5">{statusDisplay?.text}</p>
                    <p className="subtitle is-6">
                      Last checked:{" "}
                      {new Date(durinStatus?.lastChecked).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              {durinStatus?.totalArchives > 0 && (
                <div className="level-right">
                  <div className="level-item">
                    <div className="tags has-addons">
                      <span className="tag is-dark is-large">
                        <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                        Documents
                      </span>
                      <span className="tag is-info is-large">
                        {durinStatus.totalArchives}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {durinStatus?.isEmpty && (
            <div className="content mt-4">
              <h4 className="title is-5">Next Steps</h4>
              <ul>
                <li>Dataset is currently empty and ready for data ingestion</li>
                <li>Upload training data to begin model development</li>
                <li>Ensure data follows the expected schema</li>
              </ul>
            </div>
          )}

          {durinStatus?.lastArchived && (
            <div className="notification is-light mt-4">
              <p>
                <FontAwesomeIcon icon={faClock} className="mr-2" />
                Last document archived:{" "}
                {new Date(durinStatus.lastArchived).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Documents List */}
      {archives && archives.length > 0 && (
        <div className="card neumorphic">
          <div className="card-header">
            <p className="card-header-title">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
              Recent Documents
              <span className="tag is-info is-light ml-2">
                {archives.length}
              </span>
            </p>
            <div className="card-header-icon">
              <div className="field has-addons">
                <p className="control">
                  <span className="button is-static">Show</span>
                </p>
                <p className="control">
                  <span className="select">
                    <select
                      value={limit}
                      onChange={(e) =>
                        handleChangeLimit(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div
            className="card-content"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <table className="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Task ID</th>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Timestamp</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {archives.map((archive) => (
                  <tr key={archive.name}>
                    <td>
                      <code className="has-text-weight-bold">
                        {archive.taskId.substring(0, 8)}...
                      </code>
                    </td>
                    <td>{archive.name}</td>
                    <td>{(archive.size / 1024).toFixed(2)} KB</td>
                    <td>
                      <span className="icon-text">
                        <span className="icon">
                          <FontAwesomeIcon icon={faClock} />
                        </span>
                        <span>{archive.timestamp.toLocaleString()}</span>
                      </span>
                    </td>
                    <td>
                      <button
                        className="button is-small is-info"
                        onClick={() => handleViewArchive(archive)}
                        disabled={viewingArchive}
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* JSON Viewer */}
      {selectedArchive && jsonUrl && (
        <JSONViewer
          url={jsonUrl}
          close={handleCloseViewer}
          idx={0}
          label={selectedArchive.name}
        />
      )}
    </div>
  );
};

export default DatasetMonitoring;
